import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaChartBar, FaEdit, FaUser, FaList, FaSearch, FaSort, FaGraduationCap, FaArrowUp } from 'react-icons/fa';
import StudentDimensionsChart from './shared/StudentDimensionsChart';
import { DIMENSION_STYLES, UnifiedHeader } from '../styles/DimensionStyles';
import { autoconcienciaVoluntadDimension } from './AutoconcienciaVoluntad';
import { habilidadesPensamientoDimension } from './HabilidadesPensamiento';
import { habilidadesComunicacionalesDimension } from './HabilidadesComunicacionales';
import { corporalidadMovimientoDimension } from './CorporalidadyMovimiento';
import { lenguajesArtisticosDimension } from './LenguajeArtistico';
import { motivacionAprendizajeDimension } from './Motivacionaprendizaje';
import { encuentroNaturalezaDimension } from './Encuentronaturaleza';
import { habilidadesSocialesDimension } from './Habilidadessociales';
import { lenguaInglesaDimension } from './LenguaInglesa';
import { amorDiosDimension } from './AmordeDios';
import { calculateFormProgress } from '../utils/gradeCalculationForWithdrawal';
import { studentIdsMatch } from '../utils/studentIdUtils';

interface DescripcionCualitativaProps {
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleDescriptionChange: (studentId: number | string, value: string) => void;
  handleStudentSelect: (event: React.MouseEvent<HTMLButtonElement>, id: number | string) => void;
  selectedCourse: string;
  setSelectedCourse: (course: string) => void;
  courses: string[];
}

interface Student {
  id: number | string;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
  previousYearLevels?: {
    dimensionId: number;
    skillId: number;
    level: number | null;
    year: string;
  }[];
  qualitativeDescription?: string;
}

interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface Skill {
  levels: number[];
  selectedLevelIndex: number | null;
}



const DescripcionCualitativa: React.FC<DescripcionCualitativaProps> = ({ 
  students, 
  selectedStudentIds, 
  handleDescriptionChange,
  handleStudentSelect,
  selectedCourse,
  setSelectedCourse,
  courses
}) => {
  const [description, setDescription] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<"name" | "lastName" | "course">("name");

  // Funciones de utilidad para colores
  const getCompletionColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      if (percentage >= 75) return 'bg-green-600 text-white border-green-600';
      if (percentage >= 50) return 'bg-blue-600 text-white border-blue-600';
      if (percentage >= 25) return 'bg-yellow-600 text-white border-yellow-600';
      return 'bg-red-600 text-white border-red-600';
    }
    if (percentage >= 75) return 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200';
    if (percentage >= 50) return 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200';
    if (percentage >= 25) return 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200';
    return 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200';
  };

  const getPercentageTextColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) return 'text-white';
    if (percentage >= 75) return 'text-green-700';
    if (percentage >= 50) return 'text-blue-700';
    if (percentage >= 25) return 'text-yellow-700';
    return 'text-red-700';
  };

  const getFirstLastName = (fullName: string): string => {
    const names = fullName.trim().split(' ');
    if (names.length >= 2) {
      return `${names[0]} ${names[names.length - 1]}`;
    }
    return fullName;
  };

  const calculateStudentOverallCompletion = (student: Student): number => {
    // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica" para cada dimensión
    const dimensionIds = [1, 2, 3, 4, 5, 6, 7, 8, 10, 11];

    let totalValidSkills = 0;
    let completedValidSkills = 0;

    dimensionIds.forEach(dimensionId => {
      const progress = calculateFormProgress(dimensionId, student.course, student.skillLevels);
      totalValidSkills += progress.totalValidSkills;
      completedValidSkills += progress.completedSkills;
    });

    return totalValidSkills > 0 ? (completedValidSkills / totalValidSkills) * 100 : 0;
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevSelectedIdRef = useRef<number | string | null>(null);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [description]);

  useEffect(() => {
    if (selectedStudentIds.length === 1) {
      const selectedId = selectedStudentIds[0];
      const student = students.find(s => studentIdsMatch(s.id, selectedId));
      if (student) {
        setSelectedStudent(student);
        if (!studentIdsMatch(prevSelectedIdRef.current, selectedId)) {
          setDescription(student.qualitativeDescription || "");
          prevSelectedIdRef.current = selectedId;
        }
      }
    } else {
      setDescription("");
      setSelectedStudent(null);
      prevSelectedIdRef.current = null;
    }
  }, [selectedStudentIds, students]);

  const filteredStudents = useMemo(() => {
    let filtered = selectedCourse
      ? students.filter(student => student.course === selectedCourse)
      : students;

    // Aplicar filtro de búsqueda
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(student =>
        student.name.toLowerCase().includes(searchLower) ||
        student.course.toLowerCase().includes(searchLower)
      );
    }

    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "lastName":
          const lastNameA = a.name.split(' ').pop() || '';
          const lastNameB = b.name.split(' ').pop() || '';
          return lastNameA.localeCompare(lastNameB);
        case "course":
          return a.course.localeCompare(b.course);
        default:
          return 0;
      }
    });

    return filtered;
  }, [students, selectedCourse, searchTerm, sortBy]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDescription = e.target.value;
    setDescription(newDescription);
    
    if (selectedStudentIds.length === 1) {
      const studentId = selectedStudentIds[0];
      handleDescriptionChange(studentId, newDescription);
    }
    adjustTextareaHeight();
  };

  const renderStudentChart = () => {
    if (!selectedStudent) return null;
    return (
      <StudentDimensionsChart
        student={selectedStudent}
        showLegend={true}
        height={700}
        showSubdimensions={true}
        showPreviousYearLevel={true}
      />
    );
  };

  return (
    <div className={DIMENSION_STYLES.container}>
      <UnifiedHeader
        dimensionName="Registro de Trabajo Colaborativo"
        icon={FaUser}
        skillsCount={0}
        selectedCourse={selectedCourse}
        courses={courses}
        onCourseChange={setSelectedCourse}
      />
      
      {/* Sección de estudiantes con diseño idéntico a otros componentes */}
      <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
          <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Estudiante:</h3>
          
          <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
            {/* Barra de búsqueda */}
            <div className="relative w-full md:w-96 mx-auto">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                </svg>
              </div>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                placeholder="Buscar estudiante..." 
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              )}
            </div>
            
            {/* Selector de ordenamiento */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="name">Nombre</option>
                <option value="lastName">Apellido</option>
                <option value="course">Curso</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {filteredStudents.length === 0 ? (
            <div className="col-span-full text-center py-4 text-gray-500">
              No se encontraron estudiantes con "{searchTerm}"
            </div>
          ) : (
            filteredStudents.map((student) => {
              const completionPercentage = calculateStudentOverallCompletion(student);
              const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
              const isSelected = student.id && selectedStudentIds.some(sel => normalizeId(sel) === normalizeId(student.id));
              
              return (
            <button
              key={student.id}
              onClick={(event) => handleStudentSelect(event, student.id as any)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                    getCompletionColor(completionPercentage, isSelected)
                  }`}
                >
                  <div className="font-semibold" title={`${student.name} - ${student.course}`}>
              {student.name}
                  </div>
                  <div className={`text-xs font-medium ${getPercentageTextColor(completionPercentage, isSelected)}`}>
                    {completionPercentage.toFixed(1)}% completado
                  </div>
            </button>
              );
            })
          )}
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center flex-wrap">
              <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥75% completado,</span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥50% completado,</span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥25% completado,</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">&lt;25% completado</span>
              </div>
            </div>
            <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
              Tip: Ctrl + clic para seleccionar múltiples estudiantes
            </span>
          </div>
        </div>
      </div>

      {/* Layout combinado: Descripción + Gráfico en la misma línea */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Sección de descripción cualitativa */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 p-4 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <FaEdit className="text-purple-600 text-xl" />
              <h2 className="text-xl font-bold text-gray-800">Trabajo colaborativo</h2>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                <strong>Descripción cualitativa del proceso</strong> de cada niño, con estrategias para trabajar en conjunto desde la casa.
              </p>
              
              <details className="group">
                <summary className="text-sm font-semibold text-blue-800 cursor-pointer flex items-center gap-2 hover:text-blue-600">
                  <FaList className="text-blue-600 text-sm" />
                  Guía de redacción
                  <span className="ml-auto group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200">
                  <ul className="space-y-2 text-xs text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>Escribir en <strong>tercera persona</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>Evitar juicios subjetivos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>Incluir <strong>ejemplos concretos</strong> de acompañamiento</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>Vincular con los <strong>gráficos mostrados</strong></span>
                    </li>
        </ul>
                </div>
              </details>
            </div>
          </div>
          
          <div className="p-4">
            {selectedStudentIds.length === 1 ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                    <FaUser className="text-white text-sm" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-800">
                      {selectedStudent?.name}
                    </h3>
                    <p className="text-gray-600 text-sm">{selectedStudent?.course}</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-gray-200 shadow-inner">
          <textarea
            ref={textareaRef}
                    className="w-full p-4 border-none focus:outline-none bg-transparent text-gray-800 text-base leading-relaxed 
                             text-justify resize-none overflow-hidden placeholder-gray-400"
            value={description}
            onChange={handleChange}
                    placeholder="Escribe aquí la descripción cualitativa del proceso educativo del estudiante..."
                    style={{ minHeight: '300px' }}
          />
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-blue-800 text-xs">
                    <strong>💡 Consejo:</strong> Describe objetivamente avances, necesidades y estrategias específicas para el trabajo colaborativo.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FaUser className="mx-auto text-4xl text-gray-300 mb-3" />
                <h3 className="text-lg font-semibold text-gray-600 mb-1">
                  Selecciona un estudiante
                </h3>
                <p className="text-gray-500 text-sm">
                  Elige un estudiante para escribir su descripción cualitativa
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sección del gráfico de resumen */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {selectedStudentIds.length === 1 ? (
            <>
              <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 p-4 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  <FaChartBar className="text-indigo-600 text-xl" />
                  <h3 className="text-xl font-bold text-gray-800">
                    Resumen de Dimensiones
                  </h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Progreso de {selectedStudent?.name} por dimensiones
                </p>
              </div>
              
              <div className="p-4">
                {renderStudentChart()}
              </div>
            </>
          ) : (
            <div className="p-8 text-center">
              <FaChartBar className="mx-auto text-4xl text-gray-300 mb-3" />
              <h3 className="text-lg font-semibold text-gray-600 mb-1">
                Gráfico de dimensiones
              </h3>
              <p className="text-gray-500 text-sm">
                Selecciona un estudiante para ver su progreso integral
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Botón para volver arriba */}
      <div className="flex justify-center">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 
                   text-white px-6 py-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105
                   flex items-center gap-2 font-medium"
        >
          <FaArrowUp />
          Volver arriba
        </button>
      </div>
    </div>
  );
};

export default DescripcionCualitativa;
